import { useFireSessionContext } from '@chilipiper/fire-session'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { externalCalendarQueryKeys as queryKeyFactory } from '@chilipiper/integrations'
import type {
  ConnectResponse,
  DisconnectResponse,
  MailboxStatusResponse,
  MailboxStatusResponseConnected,
  MailboxStatusResponseNotConnected,
} from '@chilipiper/api-client-google-authentication'
import { useApiClient } from '@/api/apiClientContext'
import type { Clients } from '@/api/api-client/getClients'

export const useConnectIndividualGoogleIntegration = (redirectURL: string) => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { googleAuthenticationV3 } = useApiClient()

  return useMutation({
    mutationFn: () =>
      googleAuthenticationV3.connectIndividualAccountToGoogle(tenantId, {
        userId,
        redirect: redirectURL,
      }),
    onSuccess: async data => {
      await queryClient.resetQueries({
        queryKey: queryKeyFactory.calendarIntegrationStatus(userId, tenantId),
      })

      if ('location' in data) {
        window.location.assign(data.location)
      }
    },
  })
}

export const useConnectIndividualMicrosoftIntegration = (redirectURL: string) => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { microsoftAuthenticationV3 } = useApiClient()

  return useMutation({
    mutationFn: () =>
      microsoftAuthenticationV3.connectIndividualAccountToMicrosoft(tenantId, {
        userId,
        redirect: redirectURL,
      }),
    onSuccess: async data => {
      await queryClient.resetQueries({
        queryKey: queryKeyFactory.calendarIntegrationStatus(userId, tenantId),
      })

      if ('location' in data) {
        window.location.assign(data.location)
      }
    },
  })
}

// A second OAuth grant on the primary calendar account, stored and revoked separately from it (CEH-11688).
export type MailboxProvider = 'google' | 'microsoft'

export type MailboxConnectionStatus =
  | 'connected'
  | 'missingScopes'
  | 'disconnected'
  | 'notConnected'

export const mailboxQueryKeys = {
  status: (tenantId: string, userId: string, provider: MailboxProvider | undefined) =>
    ['mailbox-status', tenantId, userId, provider] as const,
}

type MailboxApi = {
  connect: (
    tenantId: string,
    query: { userId: string; redirect: string }
  ) => Promise<ConnectResponse>
  disconnect: (tenantId: string, query: { userId: string }) => Promise<DisconnectResponse>
  status: (tenantId: string, query: { userId: string }) => Promise<MailboxStatusResponse>
}

// Both auth services expose the same mailbox contract; the Google types stand in for both.
const getMailboxApi = (
  { googleAuthenticationV3, microsoftAuthenticationV3 }: Clients,
  provider: MailboxProvider
): MailboxApi =>
  provider === 'google'
    ? {
        connect: googleAuthenticationV3.connectAMailboxToGoogle,
        disconnect: googleAuthenticationV3.disconnectAMailboxFromGoogle,
        status: googleAuthenticationV3.getConnectionStatusOfAMailbox,
      }
    : {
        connect: microsoftAuthenticationV3.connectAMailboxToMicrosoft,
        disconnect: microsoftAuthenticationV3.disconnectAMailboxFromMicrosoft,
        status: microsoftAuthenticationV3.getConnectionStatusOfAMailbox,
      }

const isConnectedMailbox = (
  status: MailboxStatusResponse
): status is MailboxStatusResponseConnected => 'scopes' in status

const hasMailboxAccount = (
  status: MailboxStatusResponse
): status is MailboxStatusResponseConnected | MailboxStatusResponseNotConnected =>
  'accountId' in status

// No discriminator: only Connected has `scopes`, NotConnected still names the account, NeverConnected is empty.
export const getMailboxConnectionStatus = (
  status: MailboxStatusResponse | undefined
): MailboxConnectionStatus | undefined => {
  if (!status) return undefined
  if (isConnectedMailbox(status)) {
    return status.missingScopes?.length ? 'missingScopes' : 'connected'
  }
  return hasMailboxAccount(status) ? 'disconnected' : 'notConnected'
}

export const getMailboxEmail = (status: MailboxStatusResponse | undefined) =>
  status && hasMailboxAccount(status) ? status.email : undefined

export const useMailboxStatus = (provider: MailboxProvider | undefined) => {
  const { tenantId, userId } = useFireSessionContext()
  const clients = useApiClient()

  return useQuery({
    queryKey: mailboxQueryKeys.status(tenantId, userId, provider),
    queryFn: () => {
      if (!provider) throw new Error('Cannot read the mailbox status without a provider')
      return getMailboxApi(clients, provider).status(tenantId, { userId })
    },
    enabled: !!provider,
    refetchOnWindowFocus: true,
  })
}

export const useConnectMailbox = (provider: MailboxProvider | undefined, redirectURL: string) => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const clients = useApiClient()

  return useMutation({
    mutationFn: () => {
      if (!provider) throw new Error('Cannot connect a mailbox without a provider')
      return getMailboxApi(clients, provider).connect(tenantId, { userId, redirect: redirectURL })
    },
    onSuccess: async data => {
      if ('location' in data) {
        window.location.assign(data.location)
        return
      }
      await queryClient.invalidateQueries({
        queryKey: mailboxQueryKeys.status(tenantId, userId, provider),
      })
    },
  })
}

export const useDisconnectMailbox = (provider: MailboxProvider | undefined) => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const clients = useApiClient()

  return useMutation({
    mutationFn: () => {
      if (!provider) throw new Error('Cannot disconnect a mailbox without a provider')
      return getMailboxApi(clients, provider).disconnect(tenantId, { userId })
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: mailboxQueryKeys.status(tenantId, userId, provider),
      }),
  })
}
